/**
 * useOnboardings Hook
 *
 * Manages the list of onboardings and folders for a project.
 * Supports filtering by folder, lazy loading, and refresh.
 *
 * Sprint 3.1 - Onboardings list management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import { OnboardingFolderService } from '@modules/firebase-core/services/OnboardingFolderService';
import {
  OnboardingListItem,
  UseOnboardingsOptions,
  UseOnboardingsResult,
} from '../types/onboardings.types';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Hook for managing onboardings list
 *
 * @param options - Configuration options
 * @returns List state and actions
 *
 * @example
 * ```typescript
 * const { items, loading, error, loadMore, refresh } = useOnboardings({
 *   projectId: 'project-123',
 *   folderId: null, // null = root level
 *   pageSize: 20,
 * });
 * ```
 */
export function useOnboardings(options: UseOnboardingsOptions): UseOnboardingsResult {
  const { projectId, folderId, pageSize = DEFAULT_PAGE_SIZE } = options;

  const [items, setItems] = useState<OnboardingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Services (created once)
  const onboardingServiceRef = useRef(new OnboardingService());
  const folderServiceRef = useRef(new OnboardingFolderService());

  // Track loaded state for lazy loading
  const loadedCountRef = useRef(0);
  const allItemsLoadedRef = useRef(false);

  /**
   * Load initial data
   */
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    loadedCountRef.current = 0;
    allItemsLoadedRef.current = false;

    try {
      const onboardingService = onboardingServiceRef.current;
      const folderService = folderServiceRef.current;

      // Load folders (only at root level - folders are displayed at root)
      const folders = folderId === null
        ? await folderService.list(projectId)
        : [];

      // Load onboardings based on current folder
      let onboardings = folderId === null
        ? await onboardingService.listRootOnboardings(projectId)
        : await onboardingService.listByFolder(projectId, folderId);

      // The active Onboardings surface is V2-only. Legacy V1 documents stay
      // hidden until the external migration record is attached.
      onboardings = onboardings.filter(o => o.screenGraphType === 'v2');

      // Transform to OnboardingListItem with type
      const folderItems: OnboardingListItem[] = folders.map(folder => ({
        ...folder,
        type: 'folder' as const,
      }));

      const onboardingItems: OnboardingListItem[] = onboardings.map(onboarding => ({
        ...onboarding,
        type: 'onboarding' as const,
      }));

      // Combine: folders first, then onboardings
      const combinedItems = [...folderItems, ...onboardingItems];

      setItems(combinedItems);
      loadedCountRef.current = combinedItems.length;

      // Check if we have more to load
      // For now, if we got less than pageSize, we assume no more items
      setHasMore(onboardings.length >= pageSize);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load onboardings';
      setError(message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, folderId, pageSize]);

  /**
   * Load more items (lazy loading)
   */
  const loadMore = useCallback(async () => {
    if (loading || !hasMore || allItemsLoadedRef.current) {
      return;
    }

    setLoading(true);

    try {
      const onboardingService = onboardingServiceRef.current;

      // Load more onboardings
      const onboardings = (folderId === null
        ? await onboardingService.listRootOnboardings(projectId)
        : await onboardingService.listByFolder(projectId, folderId)
      ).filter(o => o.screenGraphType === 'v2');

      if (onboardings.length === 0) {
        setHasMore(false);
        allItemsLoadedRef.current = true;
      } else {
        const onboardingItems: OnboardingListItem[] = onboardings.map(onboarding => ({
          ...onboarding,
          type: 'onboarding' as const,
        }));

        // For simplicity, we're not implementing cursor-based pagination here
        // Just set hasMore to false to indicate all items loaded
        setHasMore(false);
        allItemsLoadedRef.current = true;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load more onboardings';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [projectId, folderId, loading, hasMore]);

  /**
   * Refresh the list
   */
  const refresh = useCallback(async () => {
    await loadData();
  }, [loadData]);

  // Load data on mount and when options change
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    items,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
  };
}
